/**
 * Valuation Wizard Pre-fill Logic
 *
 * Extracts known data from the StartupProfile and maps it to
 * ValuationInput fields so the wizard starts ~60% pre-filled.
 */

import type { StartupProfile } from '@/lib/state/profile-store';
import type { ValuationInput, ValuationStage, BusinessModel } from './types';

// ============================================================================
// Stage Mapping
// ============================================================================

/**
 * Map StartupProfile stage values to ValuationInput stage values.
 * Profile uses 'pre-seed' | 'seed' | 'series-a' (with hyphens)
 * Valuation uses 'pre_seed' | 'seed' | 'series_a' | 'series_b' | 'growth' (with underscores)
 */
function mapStage(profileStage?: string): ValuationStage | undefined {
  if (!profileStage) return undefined;

  const mapping: Record<string, ValuationStage> = {
    'pre-seed': 'pre_seed',
    'seed': 'seed',
    'series-a': 'series_a',
    'series-b': 'series_b',
    'growth': 'growth',
  };

  return mapping[profileStage];
}

// ============================================================================
// Industry to Sector Mapping
// ============================================================================

/**
 * Map profile industry strings to valuation sector strings.
 * Uses fuzzy matching to handle slight naming differences.
 */
function mapSector(industry?: string): string | undefined {
  if (!industry) return undefined;

  const lower = industry.toLowerCase();

  if (lower.includes('saas') || lower.includes('software')) return 'SaaS / Software';
  if (lower.includes('fintech') || lower.includes('financial')) return 'Fintech';
  if (lower.includes('health') || lower.includes('medtech') || lower.includes('medical')) return 'Healthcare / MedTech';
  if (lower.includes('e-commerce') || lower.includes('ecommerce') || lower.includes('commerce')) return 'E-commerce';
  if (lower.includes('ai') || lower.includes('ml') || lower.includes('machine learning') || lower.includes('artificial')) return 'AI / ML';
  if (lower.includes('clean') || lower.includes('energy') || lower.includes('climate')) return 'CleanTech / Energy';
  if (lower.includes('consumer')) return 'Consumer';
  if (lower.includes('enterprise')) return 'Enterprise';
  if (lower.includes('marketplace')) return 'Marketplace';
  if (lower.includes('hardware') || lower.includes('deep')) return 'Hardware / DeepTech';
  if (lower.includes('bio')) return 'BioTech';

  // Return the original industry if no mapping found — user can adjust
  return industry;
}

// ============================================================================
// Location to Region Mapping
// ============================================================================

/**
 * Map profile location to a valuation region.
 */
function mapRegion(location?: string): string | undefined {
  if (!location) return undefined;

  const lower = location.toLowerCase();

  // Swiss cities
  if (lower.includes('zurich') || lower.includes('zürich')) return 'Zurich';
  if (lower.includes('geneva') || lower.includes('genève') || lower.includes('geneve')) return 'Geneva';
  if (lower.includes('basel')) return 'Basel';
  if (lower.includes('bern')) return 'Bern';
  if (lower.includes('lausanne')) return 'Lausanne';
  if (lower.includes('lugano') || lower.includes('ticino')) return 'Lugano';
  if (lower.includes('switzerland') || lower.includes('suisse') || lower.includes('schweiz')) return 'Other Switzerland';

  // Broader regions
  if (lower.includes('germany') || lower.includes('austria') || lower.includes('dach')) return 'DACH Region';
  if (lower.includes('france') || lower.includes('uk') || lower.includes('spain') || lower.includes('italy') || lower.includes('europe')) return 'Western Europe';
  if (lower.includes('us') || lower.includes('usa') || lower.includes('america') || lower.includes('canada')) return 'North America';
  if (lower.includes('asia') || lower.includes('china') || lower.includes('japan') || lower.includes('india') || lower.includes('singapore')) return 'Asia Pacific';

  return undefined;
}

// ============================================================================
// Business Model Guess
// ============================================================================

/**
 * Try to guess the business model from industry and other profile data.
 */
function guessBusinessModel(profile: StartupProfile): BusinessModel | undefined {
  const industry = profile.industry?.toLowerCase() ?? '';

  if (industry.includes('saas') || industry.includes('software')) return 'saas';
  if (industry.includes('marketplace')) return 'marketplace';
  if (industry.includes('hardware') || industry.includes('deep')) return 'hardware';
  if (industry.includes('services') || industry.includes('consulting')) return 'services';

  return undefined;
}

// ============================================================================
// Pre-fill Function
// ============================================================================

/** Fields that can be pre-filled from profile */
export interface PrefilledFields {
  sector?: string;
  stage?: ValuationStage;
  region?: string;
  teamSize?: number;
  monthlyRevenue?: number;
  revenueGrowthRate?: number;
  businessModel?: BusinessModel;
  hasRevenue?: boolean;
  targetRaise?: number;
}

/**
 * Extract pre-fill data from a StartupProfile.
 *
 * Returns a partial ValuationInput that the wizard can merge
 * into its initial formData state.
 */
export function prefillFromProfile(profile: StartupProfile | null): PrefilledFields {
  if (!profile) return {};

  const prefilled: PrefilledFields = {};

  // Sector from industry
  const sector = mapSector(profile.industry);
  if (sector) prefilled.sector = sector;

  // Stage
  const stage = mapStage(profile.stage);
  if (stage) prefilled.stage = stage;

  // Region from location
  const region = mapRegion(profile.location);
  if (region) prefilled.region = region;

  // Team size (direct mapping)
  if (profile.teamSize && profile.teamSize > 0) {
    prefilled.teamSize = profile.teamSize;
  }

  // Monthly revenue
  if (profile.monthlyRevenue != null && profile.monthlyRevenue > 0) {
    prefilled.monthlyRevenue = profile.monthlyRevenue;
    prefilled.hasRevenue = true;
  }

  // Growth rate
  if (profile.growthRate != null && profile.growthRate > 0) {
    prefilled.revenueGrowthRate = profile.growthRate;
  }

  // Business model (heuristic)
  const businessModel = guessBusinessModel(profile);
  if (businessModel) prefilled.businessModel = businessModel;

  // Funding target from profile
  if (profile.fundingTarget && profile.fundingTarget > 0) {
    prefilled.targetRaise = profile.fundingTarget;
  }

  return prefilled;
}

// ============================================================================
// Count Prefilled
// ============================================================================

/** Total number of wizard fields that can potentially be pre-filled */
const TOTAL_PREFILLABLE_FIELDS = 9;

/**
 * Count how many wizard fields were successfully pre-filled from the profile.
 * Returns both the count and the total possible for display purposes.
 */
export function countPrefilled(prefilled: PrefilledFields): {
  count: number;
  total: number;
  percentage: number;
} {
  let count = 0;

  if (prefilled.sector) count++;
  if (prefilled.stage) count++;
  if (prefilled.region) count++;
  if (prefilled.teamSize != null) count++;
  if (prefilled.monthlyRevenue != null) count++;
  if (prefilled.revenueGrowthRate != null) count++;
  if (prefilled.businessModel) count++;
  if (prefilled.hasRevenue != null) count++;
  if (prefilled.targetRaise != null) count++;

  return {
    count,
    total: TOTAL_PREFILLABLE_FIELDS,
    percentage: Math.round((count / TOTAL_PREFILLABLE_FIELDS) * 100),
  };
}
